import React, { useState } from "react";
import { postRequest } from "../utils/api";
import { useConfig } from "../utils/use-config";
import { useCustomer } from "../utils/use-customer";
import Input from "./Input";
import SubmitButton from "./SubmitButton";

type Props = {};

type MessageFormErrors = {
  content?: string;
};

const MessageForm: React.FC<Props> = ({}) => {
  const {
    publishableKey,
    customerData,
    categories,
    categoryHeader,
    messagePlaceholder,
    submitText,
    successText,
    errorText,
  } = useConfig();
  const { customerToken } = useCustomer();
  const [errors, setErrors] = useState<MessageFormErrors>({});
  const [form, setForm] = useState({
    content: "",
    subject: categories ? categories[0] : "",
  });
  const [status, setStatus] = useState<
    "pending" | "sending" | "sent" | "failed"
  >("pending");

  if (status === "sent") {
    return (
      <div className="hw-flex hw-items-center hw-justify-between hw-bg-green-100 hw-text-green-700 hw-px-4 hw-py-3 hw-rounded-md hw-text-sm hw-w-full">
        {successText || "Message sent!"}
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="hw-h-5 hw-w-5 hw-flex-shrink-0 hw-ml-2"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
            clipRule="evenodd"
          />
        </svg>
      </div>
    );
  }

  return (
    <form
      className="hw-flex hw-flex-col hw-items-end"
      onSubmit={async (e) => {
        e.preventDefault();

        if (form.content.length < 1) {
          return setErrors({ content: "Please type something..." });
        }

        setStatus("sending");
        const resp = await postRequest(publishableKey, "/messages", {
          content: form.content,
          subject:
            form.subject.length > 0 ? form.subject : "New support request",
          customer_jwt: customerData || customerToken,
        });

        if (resp.status === 204) {
          setStatus("sent");
        } else {
          setStatus("failed");
        }
      }}
    >
      {!!categories && (
        <div className="hw-w-full">
          <label
            htmlFor="subject"
            className="hw-block hw-text-sm hw-font-medium hw-text-gray-700"
          >
            {categoryHeader || "I need help with..."}
          </label>
          <select
            id="subject"
            name="subject"
            value={form.subject}
            onChange={(e) => setForm({ ...form, subject: e.target.value })}
            className="hw-mt-1 hw-mb-3 hw-block hw-w-full hw-pl-3 hw-pr-10 hw-py-2 hw-text-base hw-border-gray-300 focus:hw-outline-none focus:hw-ring-indigo-500 focus:hw-border-indigo-500 sm:hw-text-sm hw-rounded-md"
          >
            {categories.map((category) => (
              <option key={category}>{category}</option>
            ))}
          </select>
        </div>
      )}
      <div className="hw-w-full">
        <label
          htmlFor="content"
          className="hw-block hw-text-sm hw-font-medium hw-text-gray-700"
        >
          Your message
        </label>
        <div className="hw-mt-1">
          <Input
            multiline
            name="content"
            id="content"
            value={form.content}
            onChange={(e) => setForm({ ...form, content: e.target.value })}
            rows={6}
            placeholder={
              messagePlaceholder || "Let us know how we can help you today..."
            }
            error={errors.content}
          />
        </div>
      </div>

      <SubmitButton
        text={status === "sending" ? "Sending..." : submitText || "Submit"}
        disabled={status === "sending"}
      />

      {status === "failed" && (
        <div className="hw-mt-3 hw-flex hw-justify-between hw-items-center hw-bg-red-100 hw-text-red-700 hw-py-3 hw-px-4 hw-rounded-md hw-text-sm hw-w-full">
          {errorText || "Message failed, try again"}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="hw-h-5 hw-w-5 hw-flex-shrink-0 hw-ml-2"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
        </div>
      )}
    </form>
  );
};

export default MessageForm;
